'use client';

import type { JSX } from 'react';
import type { HandleChangeAnswerType } from '../model';
import type { AnswerChoicesRankingType, AnswerUserType } from '~/model/AnswerUserType';
import { arrayMoveImmutable } from 'array-move';
import { useTranslations } from 'next-intl';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Loader } from '@/app/components/Loader';
import { SortableList } from '~/components/commons/SortableList/SortableList';
import { useFocus } from '~/hooks/useFocus';
import { shuffleArray } from '../utils';

export const RankingTypeQuestion = ({
  answerUser,
  answer_choices,
  questionId,
  handleChangeAnswer,
  type,
  choices_random_order,
  langValue,
  disabled = false,
}: RankingTypeQuestionPropsType) => {
  const t = useTranslations();
  const [inputRef, setInputFocus] = useFocus();
  const [arrayToRender, setArrayToRender] = useState([]);
  const [isShuffled, setIsShuffled] = useState(false);

  useEffect(() => {
    if (!isShuffled && choices_random_order) {
      setArrayToRender(
        choices_random_order ? shuffleArray(answer_choices) : answer_choices,
      );
      setIsShuffled(true);
    } else if (isShuffled) {
      setArrayToRender(value =>
        value.map(c => answer_choices.find(ac => ac.id === c.id)),
      );
    } else {
      setArrayToRender(answer_choices);
    }
  }, []);

  useLayoutEffect(() => {
    setInputFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef.current]);

  const userRanking
    = arrayToRender
      && arrayToRender.length > 0
      && answerUser
      && answerUser[questionId]
      && answerUser[questionId].answer_choices
      && (answerUser[questionId].answer_choices as AnswerChoicesRankingType)
        .ranking;

  const getItems = () => {
    return userRanking && userRanking.length > 0
      ? userRanking.map((choiceId: number) => {
          const choice = arrayToRender.find(c => c.id === +choiceId);
          return (
            <div key={choice.id}>
              <div className="label-drag">{choice && choice.title}</div>
            </div>
          );
        })
      : arrayToRender.map(choice => (
          <div lang={langValue} key={choice.id}>
            {choice.title}
          </div>
        ));
  };

  const items = getItems();

  const getRankingValues = (itemsList: JSX.Element[]) => {
    return {
      choices: arrayToRender.map(a => a['@id']),
      ranking: itemsList.map(item => Number.parseInt(item.key, 10)),
    };
  };

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    const newItems = arrayMoveImmutable(items, oldIndex, newIndex);
    handleChangeAnswer(getRankingValues(newItems), questionId, type);
  };

  return (
    <>
      {answer_choices && items && items.length > 0
        ? (
            <div className="ranking">
              {!answerUser[questionId] && (
                <div className="ranking__info">{t('FORM.RANKING_INFO')}</div>
              )}
              <div className="control-sortable" data-testid="ranking">
                <SortableList
                  items={items}
                  onSortEnd={onSortEnd}
                  disabled={disabled}
                  inputRef={inputRef}
                />
              </div>
            </div>
          )
        : (
            <Loader size="small" />
          )}
    </>
  );
};

interface RankingTypeQuestionPropsType {
  answerUser: AnswerUserType;
  answer_choices: any;
  questionId: string;
  handleChangeAnswer: HandleChangeAnswerType;
  type: string;
  choices_random_order: boolean;
  langValue: string;
  disabled?: boolean;
}
